import React, { useEffect, useState, useMemo,useContext } from "react";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import api from "../../api/axios";
import { Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert } from "@mui/material";
import { useSnackbar } from "../../utils/SnackbarProvider";
import LockIcon from '@mui/icons-material/Lock';
import { Chip } from "@mui/material";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  InputAdornment,
  LinearProgress,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Collapse,
  Fade,
} from "@mui/material";

import { Visibility, VisibilityOff, CheckCircle, ArrowForward, ArrowBack, Store, Business, LocationOn, AccountBalance, Upload, Image as ImageIcon, Schedule, Security } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import MapPicker from "./MapPicker";
import { usePhoneOTP } from "../../hooks/usePhoneOTP";
import {AuthContext} from "../../context/AuthContext";
import { v4 as uuidv4 } from "uuid";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
/* ============================
   ZOD SCHEMA (SAME PATTERN)
   ============================ */
const COUNTRY_CODES = [
  { code: "+91", label: "IN +91", validate: (d) => /^[6-9]\d{9}$/.test(d) },
  { code: "+86", label: "CN +86", validate: (d) => /^1\d{10}$/.test(d) },
];

const VendorSchema = z
  .object({
    name: z.string().min(3, "Name must be at least 3 characters"),
    email: z.string().email("Invalid email"),
    phone: z.string().min(8, "Enter a valid phone number"),

   password: z.string().optional()
  .refine(
    (val) => !val || val.length >= 6,
    {
      message: "Password must be at least 6 characters",
    }
  ),
   confirmPassword: z.string().optional(),

    // USCC and bank fields are required for new registrations (enforced via
    // stateChecks in handleNext), but optional in the Zod schema so that
    // existing vendors updating their profile are not blocked when these
    // fields are empty (e.g. vendor profile was never persisted).
    uscc: z
      .string()
      .optional()
      .refine(
        (val) => !val || val.length === 18,
        { message: "USCC must be exactly 18 characters" }
      )
      .refine(
        (val) => !val || /^[0-9A-Z]+$/.test(val),
        { message: "Only digits & capital letters" }
      ),

    bankAccountName: z.string().optional(),
    bankAccountNumber: z.string().optional(),
    bankName: z.string().optional(),
    bankBranch: z.string().optional(),
    //   specializations: z
    // .array(z.string())
    // .max(3, "Max 3 specializations")
    // .optional(),
    businessName: z.string().min(2, "Business name required").optional(),
    tagline: z.string().optional(),
    description: z.string().optional(),
    addressLine1: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    pincode: z.string().optional(),
    // location is optional — existing vendors updating their profile may not have coordinates set yet,
    // and the form would silently fail validation if this is required with an empty coordinate array.
    location: z
      .object({
        type: z.literal("Point"),
        coordinates: z.array(z.number()).length(2),
      })
      .optional(),
    market: z.string().min(1, "Please select a market"),

    aboutBusinessStory: z.string().optional(),
    aboutMission: z.string().optional(),
    aboutValues: z.string().optional(),
    yearsOfExperience: z.coerce.number().optional()  })
  .refine(
    (data) =>
      !data.password || data.password === data.confirmPassword,
    {
      message: "Passwords do not match",
      path: ["confirmPassword"],
    }
  );

/* ============================
   PASSWORD STRENGTH
   ============================ */
const getPasswordStrength = (password) => {
  let score = 0;
  if (!password) return { score: 0, color: "#d32f2f" };
  if (password.length >= 6) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[\W_]/.test(password)) score++;
  return { score, color: score >= 4 ? "#2e7d32" : "#f9a825" };
};

const COMPLIANCE_DOCS_CONFIG = {
  India: [
    { id: "compliance_gst", label: "GST Certificate" },
    { id: "compliance_pan", label: "PAN Card" },
    { id: "compliance_trade_license", label: "Trade License" },
  ],
  China: [
    { id: "compliance_business_license", label: "Business License (营业执照)" },
    { id: "compliance_legal_rep_id", label: "Legal Representative ID" },
    { id: "compliance_tax_cert", label: "Tax Registration Certificate" },
  ],
};

const STEPS = [
  { label: 'Basic Info', icon: <CheckCircle /> },
  { label: 'Company', icon: <Store /> },
  { label: 'Business Hours', icon: <Schedule /> },
  { label: 'About', icon: <Business /> },
  { label: 'Compliance', icon: <Security /> },
  { label: 'Address', icon: <LocationOn /> },
  { label: 'Bank Details', icon: <AccountBalance /> },
];

/* ============================
   PER-STEP VALIDATION CONFIG
   Each step defines which form fields to validate
   and any custom state checks before advancing.
   ============================ */
const buildStepValidation = (watchFn, phoneVerified, isNewRegistration, needsPassword, isPasswordRecovery, complianceDocs, existingComplianceDocs) => ({
  0: {
    fields: needsPassword
  ? [
      "name",
      "email",
      "phone",
      "password",
      "confirmPassword"
    ]
  : [
      "name",
      "email",
      "phone"
    ],
    stateChecks: [
      {
        check: () => phoneVerified,
        message: "Please verify your phone number before proceeding"
      },
      {
        check: () => {
          if (!needsPassword) return true;

          const pw = watchFn("password") || "";
          return pw.trim().length > 0;
        },
        message: "Password is required"
      },
      {
        check: () => {
          if (!needsPassword) return true;

          const cpw = watchFn("confirmPassword") || "";
          return cpw.trim().length > 0;
        },
        message: "Confirm Password is required"
      }
    ],
  },
  1: { fields: [], stateChecks: [] },
  2: { fields: [], stateChecks: [] },
  3: { fields: [], stateChecks: [] },
  4: {
    fields: ["market"],
    stateChecks: [
      { check: () => {
        const market = watchFn("market");
        const docs = COMPLIANCE_DOCS_CONFIG[market] || [];
        return docs.every(doc => existingComplianceDocs[doc.id] || complianceDocs[doc.id]);
      }, message: "Please upload all required compliance documents for the selected market" },
    ],
  },
  5: {
    fields: ["uscc"],
    // USCC is optional in Zod to allow profile updates with empty data.
    // Enforce non-empty at step level for new registrations.
    stateChecks: [
      {
        check: () => {
          if (!isNewRegistration && !needsPassword) return true;
          const uscc = watchFn("uscc") || "";
          return uscc.length === 18;
        },
        message: "USCC must be exactly 18 characters",
      },
    ],
  },
  6: {
    fields: ["bankAccountName", "bankAccountNumber", "bankName", "bankBranch"],
    // Bank fields are optional in Zod to allow profile updates with empty data.
    // Enforce non-empty at step level for new registrations.
    stateChecks: [
      {
        check: () => {
          if (!isNewRegistration && !needsPassword) return true;
          const name = watchFn("bankAccountName") || "";
          return name.length >= 2;
        },
        message: "Bank account holder name is required",
      },
      {
        check: () => {
          if (!isNewRegistration && !needsPassword) return true;
          const num = watchFn("bankAccountNumber") || "";
          return num.length >= 6;
        },
        message: "Bank account number is required (min 6 digits)",
      },
      {
        check: () => {
          if (!isNewRegistration && !needsPassword) return true;
          const bank = watchFn("bankName") || "";
          return bank.length >= 2;
        },
        message: "Bank name is required",
      },
      {
        check: () => {
          if (!isNewRegistration && !needsPassword) return true;
          const branch = watchFn("bankBranch") || "";
          return branch.length >= 2;
        },
        message: "Bank branch is required",
      },
    ],
  },
});

function SortableMediaItem({ item, removeMedia }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: "relative",
    borderRadius: 8,
    border: "1px solid #ddd",
    overflow: "hidden",
    background: "#fff",
    padding: 4,
  };

  return (
    <Box ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {item.type === "image" ? (
        <img
          src={item.preview}
          alt=""
          style={{
            width: "100%",
            height: 120,
            objectFit: "cover",
            borderRadius: 4,
          }}
        />
      ) : (
        <video
          src={item.preview}
          controls
          style={{
            width: "100%",
            height: 120,
            objectFit: "cover",
            borderRadius: 4,
          }}
        />
      )}

      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();   // 🔥 THIS IS THE KEY
          removeMedia(item.id, item.isExisting);
        }}
        sx={{
          position: "absolute",
          top: 5,
          right: 5,
          bgcolor: "rgba(0,0,0,0.6)",
          color: "#fff",
        }}
      >
        ✕
      </IconButton>
    </Box>
  );
}

function MediaGrid({ media, setMedia, removeMedia }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = media.findIndex((m) => m.id === active.id);
      const newIndex = media.findIndex((m) => m.id === over.id);
      setMedia(arrayMove(media, oldIndex, newIndex));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={media.map((i) => i.id)}
        strategy={verticalListSortingStrategy}
      >
<Box
  sx={{
    mt: 2,
    display: "grid",
    gap: 1.5,
    gridTemplateColumns: {
      xs: "repeat(2, 1fr)",
      sm: "repeat(auto-fill, minmax(140px, 1fr))",
    },
  }}
>
          {media.map((item) => (
            <SortableMediaItem
              key={item.id}
              item={item}
              removeMedia={removeMedia}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
}

const VendorRegistration = () => {
  const navigate = useNavigate();
    const { user,vendorProfile,loading, logout, updateUser,updateVendorProfile } = useContext(AuthContext);
  console.log(vendorProfile)
  const locationState = useLocation();
  const isNewRegistration = locationState.state?.registration === true;
  const [activeStep, setActiveStep] = useState(0);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0 });
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirmNew, setShowConfirmNew] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [vendorLogo, setVendorLogo] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const fileInputRef = useRef(null);
  const storeImageInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const [bannerFiles, setBannerFiles] = useState([]);
  const [bannerPreview, setBannerPreview] = useState("");
  const [existingBannerImages, setExistingBannerImages] = useState([]);
  // Each store image is { file: File, preview: string } so we can show instant thumbnails
  const [storeImages, setStoreImages] = useState([]);
  const [existingStoreImages, setExistingStoreImages] = useState([]);
  const [selectedSpecializations, setSelectedSpecializations] = useState([]);
  const [complianceDocs, setComplianceDocs] = useState({});
  const [existingComplianceDocs, setExistingComplianceDocs] = useState({});
  const [specializationOptions, setSpecializationOptions] = useState([]);

  // Business Hours State
  const [businessHours, setBusinessHours] = useState({
    timezone: "Asia/Kolkata",
    days: {
      monday: { open: true, startTime: "09:00", endTime: "18:00" },
      tuesday: { open: true, startTime: "09:00", endTime: "18:00" },
      wednesday: { open: true, startTime: "09:00", endTime: "18:00" },
      thursday: { open: true, startTime: "09:00", endTime: "18:00" },
      friday: { open: true, startTime: "09:00", endTime: "18:00" },
      saturday: { open: false, startTime: "10:00", endTime: "14:00" },
      sunday: { open: false, startTime: "10:00", endTime: "14:00" }
    }
  });

  const geocodeAddress = async (address) => {
  try {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    const res = await axios.get(
      "https://maps.googleapis.com/maps/api/geocode/json",
      {
        params: {
          address,
          key: apiKey,
        },
      }
    );

    if (res.data.results.length > 0) {
      const loc = res.data.results[0].geometry.location;

      return {
        type: "Point",
        coordinates: [loc.lng, loc.lat],
      };
    }

    return null;
  } catch (err) {
    console.error(err);
    return null;
  }
};

  const { showSnackbar } = useSnackbar();

  const PasswordSchema = z.object({
    currentPassword: z.string().min(6),
    newPassword: z.string().min(6),
    confirmNewPassword: z.string().min(6),
  }).refine(data => data.newPassword === data.confirmNewPassword, {
    message: "Passwords do not match",
    path: ["confirmNewPassword"],
  });

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(VendorSchema),
    mode: "onBlur",
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors },
  } = useForm({
    resolver: zodResolver(PasswordSchema),
    mode: "onBlur",
  });

  const location = watch("location");
const initialLocation = useMemo(() => {
  if (
    location?.coordinates &&
    location.coordinates.length === 2 &&
    location.coordinates[0] !== 0 &&
    location.coordinates[1] !== 0
  ) {
    return {
      latitude: location.coordinates[1],
      longitude: location.coordinates[0],
    };
  }

  return null;
}, [location]);

  // ── usePhoneOTP hook ──
  const otp = usePhoneOTP();

  // Watch phone changes and reset verification if modified
  const originalPhoneRef = useRef("");
  const phoneValue = watch("phone");
  useEffect(() => {
    if (!originalPhoneRef.current && phoneValue) {
      originalPhoneRef.current = phoneValue;
      return;
    }
    if (
      otp.phoneVerified &&
      phoneValue &&
      phoneValue !== originalPhoneRef.current
    ) {
      otp.resetOTP();
    }
  }, [phoneValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadSpecializations = async () => {
      try {
        const res = await api.get("/specializations");
        setSpecializationOptions(res.data);
      } catch (err) {
        console.error(err);
      }
    };

    loadSpecializations();
  }, []);

  

      /* ============================
     LOAD EXISTING VENDOR PROFILE
     ============================ */
  useEffect(() => {
    const loadProfile = async () => {
      try {
        //const res = await api.get("/vendor/profile");

        //const { user, vendorProfile } = res.data;
        if (user?.mobile) originalPhoneRef.current = user.mobile;
        setVendorLogo(vendorProfile?.profileImageUrl || "");

        if (vendorProfile?.complianceDocsUrls) {
          setExistingComplianceDocs(vendorProfile.complianceDocsUrls);
        }
        reset({
          name: vendorProfile?.name || "",
          email: (user?.email || "").toLowerCase(),
          phone: user?.mobile || "",
          uscc: vendorProfile?.uscc || "",
          market: vendorProfile?.market || "India",

          businessName: vendorProfile?.store?.businessName || "",
          tagline: vendorProfile?.store?.tagline || "",
          description: vendorProfile?.store?.description || "",

          addressLine1: vendorProfile?.store?.address?.addressLine1 || "",
          city: vendorProfile?.store?.address?.city || "",
          state: vendorProfile?.store?.address?.state || "",
          pincode: vendorProfile?.store?.address?.pincode || "",
          // Only set location when valid coordinates exist; otherwise leave undefined so
          // the optional Zod schema passes and the geocode fallback in onSubmit fills it.
          location: (vendorProfile?.store?.address?.location?.coordinates?.length === 2)
            ? vendorProfile.store.address.location
            : undefined,

          bankAccountName: vendorProfile?.bankDetails?.accountName || "",
          bankAccountNumber: vendorProfile?.bankDetails?.accountNumber || "",
          bankName: vendorProfile?.bankDetails?.bankName || "",
          bankBranch: vendorProfile?.bankDetails?.bankBranch || "",
          specializations: vendorProfile?.specializations || [],
          aboutBusinessStory: vendorProfile?.store?.about?.businessStory || "",
          aboutMission: vendorProfile?.store?.about?.mission || "",
          aboutValues: vendorProfile?.store?.about?.values?.join(", ") || "",
          yearsOfExperience: vendorProfile?.store?.about?.yearsOfExperience || "",
          password: "",
          confirmPassword: "",
        });
        setExistingStoreImages(
          (vendorProfile?.store?.storefrontImages || []).map((key, index) => ({
            key,
            url:
              vendorProfile?.store?.storefrontImagesUrls?.[index] ||
              key ||
              "",
          }))
        );

        // Set existing banner image if available (single image, not array)
        const bannerImageKey = vendorProfile?.store?.bannerImage;
        const bannerImageUrl = vendorProfile?.store?.bannerImageUrl;
        if (bannerImageKey) {
          setExistingBannerImages([
            {
              key: bannerImageKey,
              url: bannerImageUrl || bannerImageKey, // Use URL if available, otherwise fall back to key
            },
          ]);
        } else {
          setExistingBannerImages([]);
        }

        // Set business hours if available
        if (vendorProfile?.store?.businessHours) {
          setBusinessHours(vendorProfile.store.businessHours);
        }

        setSelectedSpecializations(
          vendorProfile?.specializations?.map((s) =>
            typeof s === "object" ? String(s._id) : String(s)
          ) || []
        );
        // Determine: password recovery vs new registration
        // Both have isRegistration=true in localStorage, but:
        //   - Password recovery: vendor already has complete profile data (just needs a password)
        //   - New registration:  vendor has no profile yet (or an empty shell), must submit full form
        const localUser = JSON.parse(localStorage.getItem("user") || "{}");
        
        if (localUser.isRegistration) {
            const hasExistingProfile = !!(
              vendorProfile?.name ||
              vendorProfile?.store?.businessName ||
              vendorProfile?.uscc
            );

            if (hasExistingProfile) {
              setNeedsPassword(true);
              setIsPasswordRecovery(true);
            } else {
              setNeedsPassword(true);
              setIsPasswordRecovery(false);
            }
          } else {
            setNeedsPassword(false);
            setIsPasswordRecovery(false);
          }
        otp.setPhoneVerified(user?.mobileVerified || false);
      } catch (err) {
        if (err.response?.status === 401) {

            navigate("/login");
            return;
        }
        console.error(err);
        } 
    };
if (loading) return;
    loadProfile();
  }, [loading, user, vendorProfile]);

  /* ============================
     PASSWORD STRENGTH WATCH
     ============================ */
  const passwordValue = watch("password");
  useEffect(() => {
    setPasswordStrength(getPasswordStrength(passwordValue));
  }, [passwordValue]);

  /* ============================
     AUTO GEOCODE ADDRESS
     ============================ */
  useEffect(() => {
    const timer = setTimeout(async () => {
      const market = watch("market");
      const address = [
        watch("addressLine1"),
        watch("city"),
        watch("state"),
        watch("pincode"),
        market || "India",
      ]
        .filter(Boolean)
        .join(", ");

      if (address.length > 10) {
        const geo = await geocodeAddress(address);

        if (geo) {
          setValue("location", geo);
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [
    watch("addressLine1"),
    watch("city"),
    watch("state"),
    watch("pincode"),
    getValues,
    setValue,
  ]);

  /* Detect country from phone number for display hint */
  const getPhoneCountryHint = (phone) => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 10 && /^[6-9]/.test(digits)) return "Will be stored as +91" + digits;
    if (digits.length === 11 && /^1/.test(digits)) return "Will be stored as +86" + digits;
    return "";
  };

  const handleSendOTP = async () => {
    const phone = watch("phone");
    if (!phone) {
      showSnackbar("Enter phone number first", "error");
      return;
    }
    const sent = await otp.handleSendOTP(phone);
    if (sent) {
      showSnackbar("OTP sent to your WhatsApp", "success");
    }
  };

  const handleVerifyOTP = async () => {
    const phone = watch("phone");
    if (!phone) return;
    const verified = await otp.handleVerifyOTP(phone, otp.otpCode);
    if (verified) {
      originalPhoneRef.current = phone;
      showSnackbar("Phone verified successfully", "success");
    }
  };

  // const handleSpecializationChange = (value) => {
  //   setSelectedSpecializations((prev) => {
  //     if (prev.includes(value)) {
  //       return prev.filter((item) => item !== value);
  //     }

  //     if (prev.length >= 3) {
  //       showSnackbar("Maximum 3 specializations allowed", "warning");
  //       return prev;
  //     }

  //     return [...prev, value];
  //   });
  // };

  const handleSpecializationToggle = (name) => {
    setSelectedSpecializations((prev) => {
      if (prev.includes(name)) {
        return prev.filter((i) => i !== name);
      }

      if (prev.length >= 3) {
        showSnackbar("You can select max 3", "warning");
        return prev;
      }

      return [...prev, name];
    });
  };
  /* ============================
     SUBMIT HANDLER
     ============================ */
  const handleRemoveExistingStoreImage = (key) => {
    setExistingStoreImages((prev) => prev.filter((image) => image.key !== key));
  };
  const removeStoreMedia = (id, isExisting) => {
  if (isExisting) {
    setExistingStoreImages((prev) =>
      prev.filter((img) => img.key !== id)
    );
  } else {
    setStoreImages((prev) =>
      prev.filter((img) => img.id !== id)
    );
  }
};

  const handleComplianceFileChange = (docId, file) => {
    if (!file) return;
    // Revoke old preview if replacing an existing new upload
    setComplianceDocs((prev) => {
      const old = prev[docId];
      if (old?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(old.preview);
      }
      return {
        ...prev,
        [docId]: { file, preview: URL.createObjectURL(file) },
      };
    });
  };

  const handleRemoveComplianceDoc = (docId) => {
    setExistingComplianceDocs((prev) => {
      const updated = { ...prev };
      delete updated[docId];
      return updated;
    });
  };

  const handleRemoveNewComplianceDoc = (docId) => {
    setComplianceDocs((prev) => {
      const entry = prev[docId];
      if (entry?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(entry.preview);
      }
      const updated = { ...prev };
      delete updated[docId];
      return updated;
    });
  };

  const handleRemoveExistingBannerImage = (key) => {
    setExistingBannerImages((prev) => prev.filter((image) => image.key !== key));
  };

  const onSubmit = async (data) => {
    // Guards: enforce step completion & phone verification ONLY for new registrations.
    //   - Password recovery (isPasswordRecovery): skip — profile already exists.
    //   - Profile update (needsPassword=false): skip — vendor is editing existing profile.
    //   - New registration (needsPassword=true, isPasswordRecovery=false): enforce.
          
    const validateAllSteps = async () => {

      for (const [stepNumber, config] of Object.entries(stepValidation)) {

        const valid = await trigger(config.fields);

        if (!valid) {
          const firstErrorField = config.fields.find(
            (field) => errors[field]
          );

          showSnackbar(
            errors[firstErrorField]?.message ||
            `Please complete ${STEPS[stepNumber].label}`,
            "warning"
          );

          return false;
        }

        for (const stateCheck of config.stateChecks) {
          if (!stateCheck.check()) {
            showSnackbar(stateCheck.message, "warning");
            return false;
          }
        }
      }

      return true;
    };
    
    const enforceStepChecks = !isPasswordRecovery && needsPassword;
      if (enforceStepChecks) {
          const isValid = await validateAllSteps();

          if (!isValid) {
            return;
          }
        }
    try {
      const user = JSON.parse(localStorage.getItem("user"));

      /* 🔐 STEP 1: SET PASSWORD (ONLY ONCE) */
      if (needsPassword) {
        if (!data.password || data.password.length < 6) {
          showSnackbar("Please set a password (at least 6 characters) to secure your account", "error");
          setActiveStep(0);
          return;
        }
        const res = await api.post("/auth/set-password", {
          username: user.username,
          password: data.password,
        });

        // 🔥 STORE TOKEN
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("user", JSON.stringify(res.data.user));
      }

      // If this is a password recovery (vendor already has a complete profile),
      // skip the full profile re-submission and go straight to dashboard
      if (isPasswordRecovery) {
        setNeedsPassword(false);
        setIsPasswordRecovery(false);
        
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        if (storedUser.isRegistration) {
          delete storedUser.isRegistration;
          localStorage.setItem("user", JSON.stringify(storedUser));
        }
        showSnackbar("Password set successfully! Redirecting to dashboard...", "success");
        navigate("/vendor/dashboard");
        return;
      }

      let finalLocation = data.location;

const hasValidCoordinates =
  finalLocation?.coordinates &&
  finalLocation.coordinates.length === 2 &&
  finalLocation.coordinates[0] !== 0 &&
  finalLocation.coordinates[1] !== 0;

if (!hasValidCoordinates) {
      const fullAddress = [
        data.addressLine1,
        data.city,
        data.state,
        data.pincode,
        data.market || "India",
      ]
    .filter(Boolean)
    .join(", ");

  const geoLocation = await geocodeAddress(fullAddress);

  if (geoLocation) {
    finalLocation = geoLocation;
  }
}
      /* 🏢 STEP 2: SAVE / UPDATE VENDOR PROFILE */
      const formData = new FormData();

      // BASIC
      formData.append("name", data.name);
      formData.append("email", data.email.toLowerCase());
      // Prepend detected country code to phone number
      const phoneDigits = data.phone.replace(/\D/g, "");
      let fullPhone = data.phone;
      if (phoneDigits.length === 10 && /^[6-9]/.test(phoneDigits)) {
        fullPhone = `+91${phoneDigits}`;
      } else if (phoneDigits.length === 11 && /^1/.test(phoneDigits)) {
        fullPhone = `+86${phoneDigits}`;
      }
      formData.append("phone", fullPhone);
      formData.append("uscc", data.uscc);
    formData.append("market", data.market);

      // COMPANY
      formData.append(
        "specializations",
        JSON.stringify(selectedSpecializations)
      );
      formData.append("businessName", data.businessName || "");
      formData.append("tagline", data.tagline || "");
      formData.append("description", data.description || "");

      const aboutPayload = {
        businessStory: data.aboutBusinessStory || "",
        mission: data.aboutMission || "",
        values: data.aboutValues
          ? data.aboutValues.split(",").map((v) => v.trim())
          : [],
        yearsOfExperience: data.yearsOfExperience
          ? Number(data.yearsOfExperience)
          : null,
      };

      formData.append("about", JSON.stringify(aboutPayload));
      // ADDRESS
      formData.append("addressLine1", data.addressLine1 || "");
      formData.append("city", data.city || "");
      formData.append("state", data.state || "");
      formData.append("pincode", data.pincode || "");
      if (finalLocation) {
        formData.append(
          "location",
          JSON.stringify(finalLocation)
        );
      }

      // BANK
      formData.append("bankAccountName", data.bankAccountName);
      formData.append("bankAccountNumber", data.bankAccountNumber);
      formData.append("bankName", data.bankName);
      formData.append("bankBranch", data.bankBranch);

      // BUSINESS HOURS
      formData.append("businessHours", JSON.stringify(businessHours));

      // FILES
      if (logoFile) formData.append("logo", logoFile);

      if (bannerFiles.length > 0) {
        formData.append("banner", bannerFiles[0]);
      }

      storeImages.forEach((img) => {
        formData.append("storeImages", img.file);
      });

      formData.append(
        "existingStorefrontImages",
        JSON.stringify(existingStoreImages.map((image) => image.key))
      );

      formData.append(
        "existingBannerImages",
        JSON.stringify(existingBannerImages.map((image) => image.key))
      );
      
      // COMPLIANCE DOCUMENTS
      Object.entries(complianceDocs).forEach(([docId, entry]) => {
        if (entry && entry.file) {
          formData.append(docId, entry.file);
        }
      });
      
      formData.append(
        "existingComplianceDocs",
        JSON.stringify(Object.keys(existingComplianceDocs))
      );

      const res= await api.put("/vendor/profile", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Clear isRegistration flag so the header shows dashboard link
      // and fetches the profile image (both gated behind !isRegistration)
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      updateVendorProfile(res.data.vendor,true);
      if (storedUser.isRegistration) {
        delete storedUser.isRegistration;
        localStorage.setItem("user", JSON.stringify(storedUser));
      }

      navigate("/vendor/dashboard");
    } catch (err) {
      // alert(err.response?.data?.message || "Failed to save vendor profile");
      showSnackbar(err.response?.data?.message || "Failed to save vendor profile", "error");
    }
  };

  const handlePasswordChange = async (data) => {
    try {
      await api.put("/auth/change-password", {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });

      showSnackbar("Password updated successfully", "success");

      resetPasswordForm();
      setOpenPasswordDialog(false);

    } catch (err) {
      showSnackbar(
        err.response?.data?.message || "Failed to update password",
        "error"
      );
    }
  };

  const getInitials = (name = "") => {
    if (!name) return "na";

    const words = name.trim().split(" ");

    if (words.length === 1) return words[0][0].toUpperCase();

    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const getAvatarColor = (name = "") => {
    const colors = [
      "#1976d2",
      "#2e7d32",
      "#ed6c02",
      "#9c27b0",
      "#0288d1",
    ];

    if (!name) return colors[0];

    let hash = 0;

    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  /* ============================
     STEP VALIDATION CONFIG (instantiated)
     ============================ */
  const stepValidation = useMemo(
    () => buildStepValidation(watch, otp.phoneVerified, isNewRegistration, needsPassword, isPasswordRecovery, complianceDocs, existingComplianceDocs),
    [watch, otp.phoneVerified, isNewRegistration, needsPassword, isPasswordRecovery, complianceDocs, existingComplianceDocs]
  );

  const handleNext = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (activeStep >= 6) return;

    const stepConfig = stepValidation[activeStep];
    if (stepConfig) {
      // 1. Validate form fields for this step using react-hook-form trigger
      if (stepConfig.fields && stepConfig.fields.length > 0) {
        const valid = await trigger(stepConfig.fields);
        if (!valid) {
            //await trigger(stepConfig.fields);
            //const currentErrors = getValues(); // force RHF refresh

            const firstErrorField = stepConfig.fields.find(
              (field) => errors[field]
            );

            if (firstErrorField && errors[firstErrorField]?.message) {
              showSnackbar(
                errors[firstErrorField].message,
                "warning"
              );
            } else {
              showSnackbar(
                "Please fix the highlighted fields",
                "warning"
              );
            }

            return;
          }
      }

      // 2. Run custom state checks (OTP, compliance docs, password match, etc.)
      if (stepConfig.stateChecks && stepConfig.stateChecks.length > 0) {
        for (const { check, message } of stepConfig.stateChecks) {
          if (!check()) {
            showSnackbar(message, "warning");
            return;
          }
        }
      }
    }

    setActiveStep((prev) => prev + 1);
  };
  const handleBack = () => setActiveStep((prev) => prev - 1);
  // Only allow navigating to already-visited steps — prevents skipping ahead
  const handleStepClick = (step) => {
          setActiveStep(step);
  };

  const renderModernField = (label, field, required = false) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
        {label} {required && <span style={{ color: '#ff9900' }}>*</span>}
      </Typography>
      {field}
    </Box>
  );

  const renderModernTextField = (props) => (
    <TextField
      {...props}
      fullWidth
      variant="outlined"
      size="small"
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          transition: 'all 0.2s ease',
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff9900',
            },
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff9900',
              borderWidth: 2,
            },
          },
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: '#ff9900',
        },
      }}
    />
  );

  const modernChipStyle = (selected) => ({
    borderRadius: 2,
    px: 1,
    py: 0.5,
    fontWeight: 500,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    transform: selected ? 'scale(1.02)' : 'scale(1)',
    boxShadow: selected ? '0 2px 8px rgba(255,153,0,0.3)' : 'none',
    bgcolor: selected ? '#ff9900' : 'transparent',
    color: selected ? '#000' : '#666',
    border: selected ? '2px solid #ff9900' : '1px solid #ddd',
    '&:hover': {
      borderColor: '#ff9900',
      bgcolor: selected ? '#ff9900' : 'rgba(255,153,0,0.08)',
    },
  });

  const modernButtonStyle = {
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 600,
    px: 3,
    py: 1,
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(255,153,0,0.2)',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(255,153,0,0.3)',
    },
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Revoke previous preview to avoid memory leaks
    if (vendorLogo && vendorLogo.startsWith("blob:")) {
      URL.revokeObjectURL(vendorLogo);
    }

    const preview = URL.createObjectURL(file);
    setVendorLogo(preview); // instant preview in UI
    setLogoFile(file); // store file for later submission with the full form
  };
  const processStoreFiles = (files) => {
  const mapped = Array.from(files).map((file) => ({
    id: uuidv4(),
    file,
    preview: URL.createObjectURL(file),
    type: "image",
  }));

  setStoreImages((prev) => [...prev, ...mapped]);
};
const handleStoreDragOver = (e) => {
  e.preventDefault();
};

const handleStoreDrop = (e) => {
  e.preventDefault();

  if (e.dataTransfer.files?.length) {
    processStoreFiles(e.dataTransfer.files);
  }
};

const combinedStoreImages = [
  ...existingStoreImages.map((img) => ({
    ...img,
    id: img.key,
    preview: img.url,
    isExisting: true,
    type: "image",
  })),

  ...storeImages.map((img) => ({
    ...img,
    id: img.id,
    preview: img.preview,
    isExisting: false,
    type: "image",
  })),
];
  return (
    <Box sx={{ minHeight: "100vh", pt: 2, pb: 4, px: 1, bgcolor: "#f8f9fa" }}>
      {/* Progress Stepper - Responsive */}
      <Box sx={{ maxWidth: 900, mx: 'auto', mb: 2, px: 1 }}>
        <Paper sx={{ p: { xs: 1, sm: 2 }, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.875rem' } } }}>
            {STEPS.map((step, index) => (
              <Step key={step.label} sx={{ cursor: 'pointer' }} onClick={() => handleStepClick(index)}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      '&.Mui-active': { color: '#ff9900' },
                      '&.Mui-completed': { color: '#4caf50' },
                      fontSize: { xs: '1.2rem', sm: '1.5rem' },
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{step.label}</Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 4 }, maxWidth: 900, mx: "auto", borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3, pb: 2, borderBottom: '2px solid #f0f0f0' }}>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#333', mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Vendor Profile
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Complete all sections to set up your vendor account
          </Typography>
          <Box sx={{ mt: 2, display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.5, bgcolor: '#fff3e0', borderRadius: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Typography variant="body2" fontWeight={600} sx={{ color: '#e65100', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Step {activeStep + 1} of {STEPS.length}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              - {STEPS[activeStep]?.label}
            </Typography>
          </Box>
        </Box>

        <form onSubmit={handleSubmit(onSubmit, (errors) => {
          // Zod validation failed — surface the first error so the user knows what to fix.
          const firstError = Object.values(errors)[0];
          const message = firstError?.message || "Please fix the highlighted fields before saving";
          showSnackbar(message, "error");
        })} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
          {/* Logo Section */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ display: 'inline-block', position: 'relative' }}>
              <Avatar
                src={vendorLogo}
                sx={{
                  width: { xs: 80, sm: 120 },
                  height: { xs: 80, sm: 120 },
                  fontSize: { xs: 32, sm: 48 },
                  fontWeight: 700,
                  bgcolor: getAvatarColor(watch("name")),
                  border: '3px solid #ff9900',
                  boxShadow: '0 4px 16px rgba(255,153,0,0.2)',
                }}
              >
                {getInitials(watch("name"))}
              </Avatar>
              <IconButton
                size="small"
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  bgcolor: '#ff9900',
                  color: '#fff',
                  '&:hover': { bgcolor: '#e68a00' },
                  boxShadow: '0 2px 8px rgba(255,153,0,0.4)',
                }}
              >
                <Upload fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
              Click to upload logo
            </Typography>
            <input
              type="file"
              hidden
              ref={fileInputRef}
              accept="image/*"
              onChange={handleLogoUpload}
            />
          </Box>

          {/* Step 0: Basic Info */}
          {activeStep === 0 && (
            <Fade in>
              <Box>
                {/* Change Password Button — only in normal edit mode (when user already has a password) */}
                {!needsPassword && !isPasswordRecovery && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                      startIcon={<LockIcon />}
                      onClick={() => setOpenPasswordDialog(true)}
                      variant="text"
                      size="small"
                      sx={{ color: '#666' }}
                    >
                      Change Password
                    </Button>
                  </Box>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  {renderModernField("Full Name *", renderModernTextField({
                    ...register("name"),
                    error: !!errors.name,
                    helperText: errors.name?.message,
                    InputLabelProps: { shrink: true },
                    placeholder: "Enter your full name",
                  }))}
                  {renderModernField("Email Address *", renderModernTextField({
                    ...register("email", { setValueAs: (v) => v?.toLowerCase?.() ?? v }),
                    error: !!errors.email,
                    helperText: errors.email?.message,
                    InputLabelProps: { shrink: true },
                    placeholder: "your@email.com",
                    type: "email",
                    inputProps: { style: { textTransform: 'lowercase' } },
                  }))}
                </Box>

                <Box sx={{ mt: 2 }}>
                  {renderModernField("Phone Number *", (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                          {renderModernTextField({
                            ...register("phone"),
                            error: !!errors.phone,
                            helperText: errors.phone?.message,
                            placeholder: "Enter phone number",
                          })}
                        </Box>
                        {!otp.phoneVerified && (
                          <Button
                            variant="contained"
                            onClick={handleSendOTP}
                            disabled={otp.otpLoading || otp.otpTimer > 0}
                            sx={{ ...modernButtonStyle, bgcolor: otp.otpTimer > 0 ? '#ccc' : '#ff9900', whiteSpace: 'nowrap', width: { xs: '100%', sm: 'auto' } }}
                          >
                            {otp.otpTimer > 0 ? `${otp.otpTimer}s` : "Send OTP"}
                          </Button>
                        )}
                        {!otp.phoneVerified && !otp.otpSent && watch("phone") && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#888', fontStyle: 'italic' }}>
                            {getPhoneCountryHint(watch("phone"))}
                          </Typography>
                        )}
                        {otp.phoneVerified && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 1, bgcolor: '#e8f5e9', borderRadius: 2, width: { xs: '100%', sm: 'auto' }, justifyContent: 'center' }}>
                            <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>Verified</Typography>
                          </Box>
                        )}
                      </Box>

                      {otp.otpSent && !otp.phoneVerified && (
                        <Fade in>
                          <Box sx={{ mt: 2, p: 2, bgcolor: '#fff8e1', borderRadius: 2, border: '1px solid #ffecb3' }}>
                            <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#f57c00' }}>
                              OTP sent to your WhatsApp
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                              <TextField
                                value={otp.otpCode}
                                onChange={(e) => otp.setOtpCode(e.target.value)}
                                placeholder="Enter 4-digit OTP"
                                size="small"
                                sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
                                inputProps={{ maxLength: 4 }}
                              />
                              <Button
                                variant="contained"
                                onClick={handleVerifyOTP}
                                sx={{ ...modernButtonStyle, bgcolor: '#ff9900', width: { xs: '100%', sm: 'auto' } }}
                              >
                                Verify
                              </Button>
                            </Box>
                          </Box>
                        </Fade>
                      )}
                    </Box>
                  ))}
                </Box>

                {needsPassword && (
                  <Box sx={{ mt: 3, p: 3, bgcolor: '#f8f9fa', borderRadius: 3, border: '1px solid #e0e0e0' }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: '#333' }}>
                      🔐 Set Your Password
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                      {renderModernField("Password", renderModernTextField({
                        type: showPassword ? "text" : "password",
                        ...register("password"),
                        error: !!errors.password,
                        helperText: errors.password?.message,
                        placeholder: "Create a strong password",
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }))}
                      {renderModernField("Confirm Password", renderModernTextField({
                        type: showConfirmPassword ? "text" : "password",
                        ...register("confirmPassword"),
                        error: !!errors.confirmPassword,
                        helperText: errors.confirmPassword?.message,
                        placeholder: "Confirm your password",
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} size="small">
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }))}
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(passwordStrength.score / 5) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: passwordStrength.color,
                            borderRadius: 4,
                          },
                        }}
                      />
                      <Typography variant="caption" sx={{ mt: 0.5, color: '#666' }}>
                        Password strength: {passwordStrength.score >= 4 ? 'Strong' : passwordStrength.score >= 2 ? 'Medium' : 'Weak'}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Fade>
          )}

          {/* Step 1: Company Details */}
          {activeStep === 1 && (
            <Fade in>
              <Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  {renderModernField("Business Name", renderModernTextField({
                    ...register("businessName"),
                    InputLabelProps: { shrink: true },
                    placeholder: "Your store name",
                  }))}
                  {renderModernField("Tagline", renderModernTextField({
                    ...register("tagline"),
                    InputLabelProps: { shrink: true },
                    placeholder: "Short tagline for your business",
                  }))}
                </Box>

                {renderModernField("About Company", renderModernTextField({
                  ...register("description"),
                  InputLabelProps: { shrink: true },
                  placeholder: "Describe your business...",
                  multiline: true,
                  rows: 3,
                }))}

                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1.5, color: '#555' }}>
                    Specializations <span style={{ color: '#ff9900' }}>(Max 3)</span>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {specializationOptions.map((item) => {
                      const selected = selectedSpecializations.includes(item.name);
                      return (
                        <Chip
                          key={item._id}
                          label={item.name}
                          clickable
                          onClick={() => handleSpecializationToggle(item.name)}
                          sx={modernChipStyle(selected)}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Fade>
          )}

          {/* Step 2: Business Hours */}
          {activeStep === 2 && (
            <Fade in>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Set your store's operating hours for customers to see
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const updated = { ...businessHours };
                      Object.keys(updated.days).forEach(day => {
                        updated.days[day].open = true;
                        updated.days[day].startTime = "09:00";
                        updated.days[day].endTime = "18:00";
                      });
                      setBusinessHours(updated);
                    }}
                    sx={{ borderColor: '#ff9900', color: '#ff9900', ...modernButtonStyle }}
                  >
                    Mon-Fri 9AM-6PM
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const updated = { ...businessHours };
                      Object.keys(updated.days).forEach(day => {
                        updated.days[day].open = false;
                      });
                      setBusinessHours(updated);
                    }}
                    sx={{ borderColor: '#666', color: '#666' }}
                  >
                    All Closed
                  </Button>
                </Box>

                {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => {
                  const dayConfig = businessHours.days[day];
                  const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);
                  return (
                    <Box key={day} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      p: 2,
                      bgcolor: dayConfig.open ? '#fffbf5' : '#fafafa',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: dayConfig.open ? '#ffecb3' : '#e0e0e0',
                      transition: 'all 0.2s ease',
                    }}>
                      <Box sx={{ width: { xs: 80, sm: 100 }, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <input
                          type="checkbox"
                          checked={dayConfig.open}
                          onChange={(e) => {
                            const updated = { ...businessHours };
                            updated.days[day].open = e.target.checked;
                            setBusinessHours(updated);
                          }}
                          style={{ marginRight: 8, width: 18, height: 18, cursor: 'pointer', accentColor: '#ff9900' }}
                        />
                        <Typography fontWeight={500} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{dayLabel}</Typography>
                      </Box>

                      {dayConfig.open ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <TextField
                            value={dayConfig.startTime}
                            onChange={(e) => {
                              const updated = { ...businessHours };
                              updated.days[day].startTime = e.target.value;
                              setBusinessHours(updated);
                            }}
                            size="small"
                            type="time"
                            sx={{ width: { xs: 100, sm: 130 } }}
                            inputProps={{ step: 300 }}
                          />
                          <Typography sx={{ color: '#666' }}>to</Typography>
                          <TextField
                            value={dayConfig.endTime}
                            onChange={(e) => {
                              const updated = { ...businessHours };
                              updated.days[day].endTime = e.target.value;
                              setBusinessHours(updated);
                            }}
                            size="small"
                            type="time"
                            sx={{ width: { xs: 100, sm: 130 } }}
                            inputProps={{ step: 300 }}
                          />
                        </Box>
                      ) : (
                        <Typography sx={{ color: '#999', fontStyle: 'italic' }}>Closed</Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Fade>
          )}

          {/* Step 3: About Business */}
          {activeStep === 3 && (
            <Fade in>
              <Box>
                {renderModernField("Business Story", renderModernTextField({
                  ...register("aboutBusinessStory"),
                  InputLabelProps: { shrink: true },
                  placeholder: "Share your business journey...",
                  multiline: true,
                  rows: 3,
                }))}

                {renderModernField("Mission", renderModernTextField({
                  ...register("aboutMission"),
                  InputLabelProps: { shrink: true },
                  placeholder: "What drives your business?",
                  multiline: true,
                  rows: 2,
                }))}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  {renderModernField("Core Values (comma separated)", renderModernTextField({
                    ...register("aboutValues"),
                    InputLabelProps: { shrink: true },
                    placeholder: "Quality, Innovation, Trust",
                  }))}
                  {renderModernField("Years of Experience", renderModernTextField({
                    ...register("yearsOfExperience"),
                    InputLabelProps: { shrink: true },
                    placeholder: "e.g., 5",
                    type: "number",
                  }))}
                </Box>

                {/* Store Banner */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1.5, color: '#555' }}>
                    Store Banner
                  </Typography>
                  <Box
                    onClick={() => bannerInputRef.current?.click()}
                    sx={{
                      border: "2px dashed #ddd",
                      borderRadius: 3,
                      p: 3,
                      textAlign: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "#ff9900",
                        bgcolor: "#fffbf5",
                      },
                    }}
                  >
                    <input
                      ref={bannerInputRef}
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        if (bannerPreview) {
                          URL.revokeObjectURL(bannerPreview);
                        }

                        setBannerFiles([file]);
                        setBannerPreview(URL.createObjectURL(file));
                      }}
                    />

                    <ImageIcon
                      sx={{
                        fontSize: 48,
                        color: "#999",
                        mb: 1,
                      }}
                    />

                    <Typography fontWeight={500}>
                      Drop banner image or click to browse
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "#777",
                        mt: 1,
                      }}
                    >
                      Recommended size: 1200 × 300 px
                    </Typography>
                  
                  {(existingBannerImages.length > 0 || bannerPreview) && (
                    <Box sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          position: "relative",
                          borderRadius: 2,
                          overflow: "hidden",
                          border: "1px solid #ddd",
                        }}
                      >
                        <img
                          src={
                            bannerPreview ||
                            existingBannerImages?.[0]?.url
                          }
                          alt="Banner"
                          style={{
                            width: "100%",
                            height: "220px",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />

                        <IconButton
                          onClick={() => {
                            if (bannerPreview) {
                              URL.revokeObjectURL(bannerPreview);
                              setBannerPreview("");
                              setBannerFiles([]);
                            }

                            if (existingBannerImages.length > 0) {
                              handleRemoveExistingBannerImage(
                                existingBannerImages[0].key
                              );
                            }
                          }}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: "rgba(0,0,0,0.6)",
                            color: "#fff",
                            "&:hover": {
                              bgcolor: "rgba(0,0,0,0.8)",
                            },
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Box>
                </Box>
                {/* Store Images */}
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{ mb: 1.5, color: "#555" }}
                  >
                    Store Images
                  </Typography>

                  <Box
                    onDragOver={handleStoreDragOver}
                    onDrop={handleStoreDrop}
                    onClick={() =>
                      storeImageInputRef.current?.click()
                    }
                    sx={{
                      border: "2px dashed #ddd",
                      borderRadius: 3,
                      p: 3,
                      textAlign: "center",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: "#ff9900",
                        bgcolor: "#fffbf5",
                      },
                    }}
                  >
                    <input
                      ref={storeImageInputRef}
                      type="file"
                      hidden
                      multiple
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files?.length) {
                          processStoreFiles(e.target.files);
                        }
                      }}
                    />

                    <Typography>
                      Drop store images or click to browse
                    </Typography>

                    <MediaGrid
                      media={combinedStoreImages}
                      setMedia={(updated) => {
                        setStoreImages(
                          updated.filter((m) => !m.isExisting)
                        );
                      }}
                      removeMedia={removeStoreMedia}
                    />
                  </Box>
                </Box>
              </Box>
            </Fade>
          )}

          {/* Step 4: Compliance */}
          {activeStep === 4 && (
            <Fade in>
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    Select Market <span style={{ color: '#ff9900' }}>*</span>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    {['India', 'China'].map((market) => (
                      <Box
                        key={market}
                        onClick={() => setValue("market", market, { shouldValidate: true, shouldDirty: true })}
                        sx={{
                          flex: 1,
                          p: 2,
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: watch("market") === market ? '#ff9900' : '#ddd',
                          bgcolor: watch("market") === market ? '#fffbf5' : '#fff',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s ease',
                          '&:hover': { borderColor: '#ff9900' },
                        }}
                      >
                        <Typography fontWeight={watch("market") === market ? 600 : 400} color={watch("market") === market ? '#ff9900' : '#333'}>
                          {market}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  {errors.market && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.market.message}
                    </Typography>
                  )}
                </Box>

                <Typography variant="body2" fontWeight={500} sx={{ mb: 2, color: '#555' }}>
                  Required Documents
                </Typography>

                {(COMPLIANCE_DOCS_CONFIG[watch("market")] || []).map((doc) => (
                  <Box key={doc.id} sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    bgcolor: '#fafafa',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': { borderColor: '#ff9900', bgcolor: '#fffbf5' },
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={500}>{doc.label}</Typography>
                      {existingComplianceDocs[doc.id] && (
                        <Typography variant="caption" sx={{ color: '#4caf50' }}>
                          ✓ Uploaded
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* Show existing backend document preview */}
                      {existingComplianceDocs[doc.id] && (
                        <>
                          {(() => {
                            const fileUrl = existingComplianceDocs[doc.id];
                            const ext = fileUrl ? fileUrl.split('.').pop().toLowerCase().split('?')[0] : '';
                            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
                            const isPdf = ext === 'pdf';
                            if (isImage) {
                              return <img src={fileUrl} alt="Preview" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} onError={(e) => e.target.style.display = 'none'} />;
                            }
                            return isPdf ? <PictureAsPdfIcon sx={{ color: '#d32f2f' }} /> : <DescriptionIcon sx={{ color: '#607d8b' }} />;
                          })()}
                          <IconButton size="small" onClick={() => handleRemoveComplianceDoc(doc.id)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {/* Show newly uploaded document preview (instant thumbnail) */}
                      {complianceDocs[doc.id] && !existingComplianceDocs[doc.id] && (
                        <>
                          {(() => {
                            const entry = complianceDocs[doc.id];
                            const isImage = entry.file.type.startsWith("image/");
                            const isPdf = entry.file.type === "application/pdf";
                            if (isImage && entry.preview) {
                              return <img src={entry.preview} alt="New Preview" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />;
                            }
                            return isPdf ? <PictureAsPdfIcon sx={{ color: '#d32f2f' }} /> : <DescriptionIcon sx={{ color: '#607d8b' }} />;
                          })()}
                          <IconButton size="small" onClick={() => handleRemoveNewComplianceDoc(doc.id)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {/* Show newly uploaded document replacing an existing one */}
                      {complianceDocs[doc.id] && existingComplianceDocs[doc.id] && (
                        <>
                          {(() => {
                            const entry = complianceDocs[doc.id];
                            const isImage = entry.file.type.startsWith("image/");
                            const isPdf = entry.file.type === "application/pdf";
                            if (isImage && entry.preview) {
                              return <img src={entry.preview} alt="New Preview" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />;
                            }
                            return isPdf ? <PictureAsPdfIcon sx={{ color: '#d32f2f' }} /> : <DescriptionIcon sx={{ color: '#607d8b' }} />;
                          })()}
                          <IconButton size="small" onClick={() => handleRemoveNewComplianceDoc(doc.id)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        sx={{ borderColor: '#ff9900', color: '#ff9900' }}
                      >
                        {existingComplianceDocs[doc.id] ? 'Change' : 'Upload'}
                        <input
                          type="file"
                          hidden
                          onChange={(e) => handleComplianceFileChange(doc.id, e.target.files[0])}
                        />
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Fade>
          )}

          {/* Step 5: Address */}
          {activeStep === 5 && (
            <Fade in>
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1.5, color: '#555' }}>
                    📍 Business Location
                  </Typography>
                  <MapPicker setValue={setValue} initialLocation={initialLocation} />
                </Box>

                {renderModernField("Street Address", renderModernTextField({
                  ...register("addressLine1"),
                  InputLabelProps: { shrink: true },
                  placeholder: "Enter your business address",
                }))}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  {renderModernField("City", renderModernTextField({
                    ...register("city"),
                    InputLabelProps: { shrink: true },
                    placeholder: "City",
                  }))}
                  {renderModernField("State", renderModernTextField({
                    ...register("state"),
                    InputLabelProps: { shrink: true },
                    placeholder: "State",
                  }))}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  {renderModernField("Pincode", renderModernTextField({
                    ...register("pincode"),
                    InputLabelProps: { shrink: true },
                    placeholder: "Postal code",
                  }))}
                  {renderModernField("USCC *", renderModernTextField({
                    ...register("uscc"),
                    error: !!errors.uscc,
                    helperText: errors.uscc?.message,
                    InputLabelProps: { shrink: true },
                    placeholder: "18-character USCC",
                  }))}
                </Box>
                <input type="hidden" {...register("latitude")} />
                <input type="hidden" {...register("longitude")} />
              </Box>
            </Fade>
          )}

          {/* Step 6: Bank Details */}
          {activeStep === 6 && (
            <Fade in>
              <Box>
                <Box sx={{
                  p: 3,
                  mb: 3,
                  bgcolor: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)',
                  borderRadius: 3,
                  border: '1px solid #ffecb3',
                }}>
                  <Typography variant="body2" sx={{ color: '#f57c00' }}>
                    💰 Your bank details are securely stored and used for settlements
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  {renderModernField("Account Holder Name", renderModernTextField({
                    ...register("bankAccountName"),
                    InputLabelProps: { shrink: true },
                    placeholder: "As per bank records",
                  }))}
                  {renderModernField("Account Number", renderModernTextField({
                    ...register("bankAccountNumber"),
                    InputLabelProps: { shrink: true },
                    placeholder: "Your account number",
                  }))}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  {renderModernField("Bank Name", renderModernTextField({
                    ...register("bankName"),
                    InputLabelProps: { shrink: true },
                    placeholder: "Bank name",
                  }))}
                  {renderModernField("Bank Branch", renderModernTextField({
                    ...register("bankBranch"),
                    InputLabelProps: { shrink: true },
                    placeholder: "Branch name",
                  }))}
                </Box>
              </Box>
            </Fade>
          )}

          {/* Navigation Buttons */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            justifyContent: 'space-between',
            mt: 3,
            pt: 2,
            borderTop: '2px solid #f0f0f0',
            gap: { xs: 2, sm: 0 }
          }}>
            <Button
              type="button"
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<ArrowBack />}
              sx={{
                borderRadius: 2,
                px: 3,
                borderColor: '#ddd',
                color: '#666',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': { borderColor: '#ff9900', color: '#ff9900' },
              }}
            >
              Back
            </Button>

            {activeStep >= 6 ? (
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                sx={{ ...modernButtonStyle, bgcolor: '#4caf50', px: 4, width: { xs: '100%', sm: 'auto' } }}
              >
                {isSubmitting ? 'Saving...' : 'Save Vendor Profile ✓'}
              </Button>
            ) : (
              <Button
                type="button"
                variant="contained"
                onClick={(e) => handleNext(e)}
                disabled={activeStep >= 6}
                endIcon={<ArrowForward />}
                sx={{ ...modernButtonStyle, bgcolor: activeStep >= 6 ? '#ccc' : '#ff9900' }}
              >
                Next
              </Button>
            )}
          </Box>
        </form>

        <Dialog
          open={openPasswordDialog}
          onClose={() => setOpenPasswordDialog(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, p: 1 }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
              <LockIcon sx={{ color: '#ff9900' }} />
              <Typography variant="h6" fontWeight={600}>Change Password</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Current Password"
              type={showCurrent ? "text" : "password"}
              margin="normal"
              variant="outlined"
              {...registerPassword("currentPassword")}
              error={!!passwordErrors.currentPassword}
              helperText={passwordErrors.currentPassword?.message}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowCurrent(!showCurrent)}>
                      {showCurrent ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="New Password"
              type={showNew ? "text" : "password"}
              margin="normal"
              variant="outlined"
              {...registerPassword("newPassword")}
              error={!!passwordErrors.newPassword}
              helperText={passwordErrors.newPassword?.message}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNew(!showNew)}>
                      {showNew ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <LinearProgress
              variant="determinate"
              value={(passwordStrength.score / 5) * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                my: 1,
                bgcolor: "#eee",
                "& .MuiLinearProgress-bar": {
                  bgcolor: passwordStrength.color,
                  borderRadius: 4,
                },
              }}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type={showConfirmNew ? "text" : "password"}
              margin="normal"
              variant="outlined"
              {...registerPassword("confirmNewPassword")}
              error={!!passwordErrors.confirmNewPassword}
              helperText={passwordErrors.confirmNewPassword?.message}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmNew(!showConfirmNew)}>
                      {showConfirmNew ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setOpenPasswordDialog(false)}
              sx={{ borderRadius: 2, color: '#666' }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handlePasswordSubmit(handlePasswordChange)}
              sx={{ ...modernButtonStyle, bgcolor: '#ff9900', borderRadius: 2 }}
            >
              Update Password
            </Button>
          </DialogActions>
        </Dialog>
        {/* <Snackbar
  open={snackbarOpen}
  autoHideDuration={3000}
  onClose={() => setSnackbarOpen(false)}
>
  <Alert severity="success" sx={{ width: "100%" }}>
    Password updated successfully
  </Alert>
</Snackbar> */}
      </Paper>
    </Box>
  );
};

export default VendorRegistration;
